import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import { type ReactElement, type Ref, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { Link, useLocation, useMatch } from 'react-router-dom';

import { Footer } from '@workspace/shared/components/Footer';
import { Header } from '@workspace/shared/components/Header';

import AppIcon from '~/assets/img/appIcon.png';
import { company } from '~/company/Company';
import { CompanyName } from '~/company/interface';
import FooterSaas from '~/components/Footer/Footer';
import { FooterMini } from '~/components/FooterMini/FooterMini';
import { HeaderDubaiExpress } from '~/components/HeaderDubaiExpress';
import Icon from '~/components/Icon/Icon';
import PageHeader from '~/components/isolated/PageHeader';
import { EmptyHeader, MobileHeaderCD } from '~/components/MobileHeaderCD';
import { HeaderAddressButton } from '~/components/mcosmetic/header/HeaderAddressButton';
import { HeaderNavigation } from '~/components/mcosmetic/header/HeaderNavigation';
import { useGlobal } from '~/hooks/useGlobal';
import { useHeaderHeight } from '~/hooks/useHeaderHeight';
import { useIsRN } from '~/hooks/useIsRN';
import { MODAL_EDIT_ADDRESS_PARAM, useModal } from '~/hooks/useModal';
import { useStores } from '~/hooks/useStore';
import { getDisplayOrderNumber } from '~/pages/DubaiExpress/customerStatus';
import { mainStore, orderStore, userStore } from '~/stores';
import { CatalogCategory } from '~/stores/CategoriesStore';
import { getAddressModalParams } from '~/utils/addressModal';
import { isIOS } from '~/utils/platform';

import { BottomNavigationBar } from './BottomNavigationBar';
import BottomNavigationBarV2 from './BottomNavigationBar/BottomNavigationBarV2';
import { ROUTES } from './constants';
import styles from './MobileLayout.module.scss';
import { getPageHeaderConfig } from './pageHeaderConfig';

interface MobileLayoutProps {
  children: ReactElement;
  layoutRef: Ref<HTMLDivElement>;
}

export const MobileDownloadHeader = () => {
  const [visible, setVisible] = useState(true);
  const { t } = useTranslation();

  const handleCloseClick = () => {
    setVisible(false);
  };

  const handleDownloadClick = () => {
    setVisible(false);
    mainStore.setIsAppInstalled(true);
  };

  if (!visible) {
    return null;
  }

  return (
    <div className={styles.mobileHeaderContainer}>
      <div className={styles.mobileHeaderContentContainer}>
        <div onClick={handleCloseClick}>
          <Icon type="close" />
        </div>
        <img className={styles.mobileHeaderIcon} src={AppIcon} alt="" />
        <div className={styles.descriptionContainer}>{t('downloadApp')}</div>
      </div>

      <a
        href={
          isIOS()
            ? company.config.appStoreUrl.ios
            : company.config.appStoreUrl.android
        }
        target="_blank"
        className="button _primary product-card__buy small"
        rel="noreferrer"
        onClick={handleDownloadClick}
      >
        {mainStore.isAppInstalled
          ? t('phrases:openApp')
          : t('phrases:download')}
      </a>
    </div>
  );
};

const MobileLayout = observer(({ children, layoutRef }: MobileLayoutProps) => {
  const { isMobile } = useGlobal();
  const { pathname } = useLocation();
  const isOrderPaymentPage = !!useMatch(ROUTES.order_payment);
  const { t } = useTranslation();
  const { openModal } = useModal();
  const { heroBanner } = useStores();

  const headerRef = useRef<HTMLHeadingElement>(null);

  const isProductPage = /^\/p\/(.*?)$/gi.test(pathname);
  const isSearchPage = pathname === '/search';
  const isHomePage = pathname === '/';
  const isProfilePage = pathname === '/cabinet';

  const isHeaderHidden = useMemo(
    () =>
      !company.newDesignSystemEnabled ||
      (company.newDesignSystemEnabled && isProductPage) ||
      (company.newDesignSystemEnabled &&
        company.is('mcosmetic') &&
        isProfilePage),
    [isProductPage, isProfilePage, company.newDesignSystemEnabled],
  );

  /**
   * CityDrinks opens the product and order pages with a full-bleed block that
   * has to meet the header, so the content sheet drops the top gap it reserves.
   * The rounding joining the black header to the content is kept — the
   * stylesheet paints it over the content rather than clipping the sheet, see
   * `.content.flushTop`.
   */
  const isFlushTop = useMemo(
    () =>
      isHeaderHidden ||
      (company.is(CompanyName.CityDrinks) && pathname === ROUTES.checkout),
    [isHeaderHidden, pathname],
  );

  const isSharp = useMemo(() => {
    return (
      isHeaderHidden &&
      company.match({
        mcosmetic: !isProfilePage,
        default: true,
      })
    );
  }, [isHeaderHidden, isProfilePage]);

  const handleDeliveryAddress = () => {
    const [targetModal, source, params] = getAddressModalParams('header', {
      [MODAL_EDIT_ADDRESS_PARAM]: userStore?.deliveryAddress?.addressId || '',
    });

    openModal(targetModal, source, params);
  };

  const header = (() => {
    const pageHeaderConfig = getPageHeaderConfig(pathname, true, t);
    if (company.newDesignSystemEnabled && pageHeaderConfig) {
      if (pageHeaderConfig.hideOnMobile) {
        return <EmptyHeader ref={headerRef} />;
      }

      if (pageHeaderConfig.useCategoryName) {
        const categorySlug = pathname.split('/c/')[1]?.split('/')[0];
        if (categorySlug) {
          const category = CatalogCategory.getInstance(categorySlug);
          const categoryName = category.data?.name ?? (
            <Skeleton containerClassName={styles.categoryNameSkeleton} />
          );

          return (
            <PageHeader
              ref={headerRef}
              title={categoryName}
              backgroundImage={category.data?.previewImage}
            />
          );
        }
      }

      if (isHeaderHidden) return null;

      const viewedOrder = orderStore.viewedOrder;
      const title =
        company.is(CompanyName.DubaiExpress) &&
        pathname.startsWith('/order/') &&
        viewedOrder
          ? t('cabinetDE:orderNumber', {
              number: getDisplayOrderNumber(viewedOrder),
            })
          : pageHeaderConfig.title;

      return (
        <PageHeader
          ref={headerRef}
          title={title}
          showBackButton={pageHeaderConfig.showBackButton}
          showSupportButton={pageHeaderConfig.showSupportButton}
        />
      );
    }

    if (isHeaderHidden) {
      return null;
    }

    if (company.newDesignSystemEnabled && isSearchPage) {
      return <EmptyHeader ref={headerRef} />;
    }

    return company.match({
      mcosmetic: (
        <Header.MCosmetic
          logoWrapper={({ children }) => <Link to="/">{children}</Link>}
          logo={{
            mobile: company.images.logoMobile?.vector,
            desktop: company.images.logo.vector,
          }}
          ref={headerRef}
          shouldChangeColorOnScroll={isHomePage}
        >
          <Header.Address>
            <HeaderAddressButton onClick={handleDeliveryAddress} />
          </Header.Address>
          <Header.Navigation>
            <HeaderNavigation />
          </Header.Navigation>
        </Header.MCosmetic>
      ),
      dubaiexpress: <HeaderDubaiExpress ref={headerRef} />,
      default: <MobileHeaderCD ref={headerRef} />,
    });
  })();

  const footer = (() => {
    if (isOrderPaymentPage) {
      return null;
    }

    const footer = (
      <Footer.FooterBaseSaas
        logo={{ desktop: company.images.logo.vector }}
        name={company.config.name}
      />
    );
    const footerComp = company.match({
      mcosmetic: footer,
      heimkaup: footer,
      localee: footer,
      circlek: footer,
      default: <FooterSaas />,
    });

    if (!company.hasNewCheckoutElemtnts) {
      return footerComp;
    }

    switch (pathname) {
      case ROUTES.checkout:
        return <FooterMini />;
      default:
        return footerComp;
    }
  })();

  useHeaderHeight(headerRef, [pathname], isHeaderHidden);

  const isRN = useIsRN();

  return (
    <>
      <div
        className={classes([
          styles.mobileLayout,
          company.newDesignSystemEnabled && styles.redesign,
          isSharp && styles.sharp,
        ])}
        ref={layoutRef}
      >
        {isMobile &&
          !isRN &&
          company.showMobileDowloadPanel &&
          !company.newDesignSystemEnabled && <MobileDownloadHeader />}
        {header}
        {company.newDesignSystemEnabled ? (
          <div
            className={classes([
              styles.content,
              heroBanner.shouldShowSection &&
                pathname === ROUTES.index &&
                styles.withHeroBanner,
              company.isDubaiExpressCompany &&
                pathname === ROUTES.checkout &&
                styles.checkoutContent,
              isFlushTop && styles.flushTop,
            ])}
          >
            {children}
          </div>
        ) : (
          children
        )}
        {!isRN && footer}
      </div>
      {isOrderPaymentPage ? null : pathname === '/checkout' ? (
        company.newDesignSystemEnabled ? null : (
          <BottomNavigationBar />
        )
      ) : company.newDesignSystemEnabled ? (
        <BottomNavigationBarV2 />
      ) : (
        <BottomNavigationBar />
      )}
    </>
  );
});

export default MobileLayout;
